// src/services/adsApi.ts
import axios from "./axyos";
import { Ad, AdPayload } from "../features/ads/adsTypes";
import i18n from "../i18n";
import type { CaptchaPayload } from "./registerApi";

const API_URL = "/addAds";

const API_LOAD_AD_URL = "/loadOneAds";

const API_UPDATE_AD = "/updateAds";

const API_ALL_ADS = "/loadAllAds";
const API_ADMIN_ADS_TOP_SEARCH = "/admin/ads/top-search";

const API_SAVE_BOOKING = "/saveBookingByAds";
const API_VALIDATE_BOOKING_BY_DATE = "/validateBookingByDate";
const API_CANCEL_BOOKING = "/cancelBooking";
const API_UPDATE_BOOKING_PRICE_BY_DATE = "/updateBookingPriceByDate";
const API_SET_BOOKING_DATES_BLOCKED_BY_OWNER = "/setBookingDatesBlockedByOwner";
const API_ADMIN_DELETE_BOOKINGS_BY_USER = "/admin/deleteBookingsByUserId";
const API_ADS_FAVORITE = "/ads";
const API_LIST_BOOKING_BY_AD_AND_BY_USER = "/listBookingByAdAndByUser";
const API_BOOKINGS = "/bookings";

const buildAdFormData = ({
  ad,
  photoFiles,
  userId,
  user,
  captcha,
  sourceLanguage,
}: {
  ad: Ad;
  photoFiles?: File[];
  userId?: string;
  user?: AdPayload["user"] | { status?: string; nbr_child?: number };
  captcha?: CaptchaPayload;
  sourceLanguage: string;
}) => {
  const formData = new FormData();
  const { photoFiles: adPhotoFiles = [], ...serializableAd } = ad || ({} as Ad);
  const filesToAppend = Array.isArray(photoFiles) ? photoFiles : adPhotoFiles;

  formData.append("ad", JSON.stringify({
    ...serializableAd,
    sourceLanguage,
  }));

  if (userId) {
    formData.append("userId", userId);
  }
  if (user) {
    formData.append("user", JSON.stringify(user));
  }
  if (captcha) {
    formData.append("captcha", JSON.stringify(captcha));
  }

  filesToAppend.forEach((file) => {
    formData.append("photos", file);
  });

  return formData;
};

export const createAdApi = (payload: AdPayload) => {
  const sourceLanguage = (i18n.resolvedLanguage || "fr").split("-")[0];
  const formData = buildAdFormData({
    ad: payload.ad,
    photoFiles: payload.ad?.photoFiles,
    userId: payload.userId,
    user: payload.user,
    captcha: payload.captcha,
    sourceLanguage,
  });
  return axios.post(API_URL, formData, {
    params: { lang: sourceLanguage },
    headers: { 'Content-Type': undefined },
  });
};

export const updateAdApi = (id: string, payload: AdPayload) => {
  const sourceLanguage = (i18n.resolvedLanguage || "fr").split("-")[0];
  const formData = buildAdFormData({
    ad: payload.ad,
    photoFiles: payload.ad?.photoFiles,
    sourceLanguage,
  });

  return axios.put(`${API_UPDATE_AD}/${id}`, formData, {
    params: { lang: sourceLanguage },
    headers: { 'Content-Type': undefined },
  });
};

export const getAdByIdApi = (id: string, signal?: AbortSignal) => {
  const lang = (i18n.resolvedLanguage || "fr").split("-")[0];
  return axios.get<Ad>(`${API_LOAD_AD_URL}/${id}`, { params: { lang }, signal });
};

export const getAdsApi = () => {
  const lang = (i18n.resolvedLanguage || "fr").split("-")[0];
  return axios.get<Ad[] | { data: Ad[] }>(API_ALL_ADS, { params: { lang } });
};

export const getPaginatedAdsApi = (params: {
  limit?: number;
  cursor?: string;
  idOwner?: string;
  name?: string;
  area?: number;
  room_nbr?: number;
  min_nights?: number;
  nbr_child?: number;
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  status?: boolean;
  signal?: AbortSignal;
}) => {
  const lang = (i18n.resolvedLanguage || "fr").split("-")[0];
  const { signal, ...queryParams } = params;
  return axios.get<{
    data: Ad[];
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
    status: number;
  }>(API_ALL_ADS, { params: { ...queryParams, lang }, signal });
};

export const updateAdStatusApi = (id: string, status: boolean) => {
  const sourceLanguage = (i18n.resolvedLanguage || "fr").split("-")[0];
  return axios.put(`${API_UPDATE_AD}/${id}`, {
    ad: { status, sourceLanguage },
  }, { params: { lang: sourceLanguage } });
};

export type TopAdSearchItem = {
  id: string;
  title: string;
  title_i18n?: Record<"fr" | "en" | "ar", string>;
  top: number | null;
  rate: number | null;
  status: boolean;
  created_at: string | null;
};

export const searchAdsForTopPositionApi = (query: string, limit = 8) =>
  axios.get<{ data: TopAdSearchItem[]; count: number; status: number }>(API_ADMIN_ADS_TOP_SEARCH, {
    params: { q: query, limit },
  });

export const updateAdTopPositionApi = (id: string, top: number | null) =>
  axios.patch<{ data: TopAdSearchItem; status: number }>(`/admin/ads/${encodeURIComponent(id)}/top`, { top });

export const updateAdRateApi = (id: string, rate: number) =>
  axios.patch<{ data: TopAdSearchItem; status: number }>(`/admin/ads/${encodeURIComponent(id)}/rate`, { rate });

export const saveBookingByAdsApi = (payload: any) => {
  return axios.post(API_SAVE_BOOKING, payload);
};

export const validateBookingByDateApi = (payload: {
  id_user: string;
  id_ads: string;
  date: string;
  booked: boolean;
  groupe?: string;
  bookingDates?: string[];
}) => {
  return axios.post(API_VALIDATE_BOOKING_BY_DATE, payload);
};

export const cancelBookingByDatesApi = (payload: {
  id_user: string;
  id_ads: string;
  bookingDates?: string[];
  groupe?: string;
  cancelledByOwner?: boolean;
}) => {
  return axios.post(API_CANCEL_BOOKING, payload);
};

export const updateBookingPriceByDateApi = (payload: {
  id_user: string;
  id_ads: string;
  date?: string;
  bookingDates?: string[];
  priceByDay: number;
}) => {
  return axios.post(API_UPDATE_BOOKING_PRICE_BY_DATE, payload);
};

export const setBookingDatesBlockedByOwnerApi = (payload: {
  id_user: string;
  id_ads: string;
  date?: string;
  bookingDates?: string[];
  blocked: boolean;
}) => {
  return axios.post(API_SET_BOOKING_DATES_BLOCKED_BY_OWNER, payload);
};

export const deleteBookingsByUserIdApi = (payload: { id_user: string }) => {
  return axios.post(API_ADMIN_DELETE_BOOKINGS_BY_USER, payload);
};

export const toggleAdFavoriteApi = (id: string) => {
  return axios.post(`${API_ADS_FAVORITE}/${id}/favorite`, {});
};

export const getBookingsByAdApi = (id_ads: string) => {
  return axios.get<{
    data: Array<{
      _id?: string;
      user?: any;
      bookingDates?: Array<{ date?: string; booked?: boolean | null } | string>;
    }>;
    count?: number;
    hasMore?: boolean;
    nextCursor?: string | null;
    status?: number;
  }>(API_LIST_BOOKING_BY_AD_AND_BY_USER, { params: { id_ads } });
};

export const getBookingsByUserApi = (id_user: string, params?: { limit?: number; cursor?: string | null }) => {
  return axios.get<{
    data: Array<{
      _id?: string;
      user?: any;
      ads?: any;
      bookingDates?: Array<{ date?: string; booked?: boolean | null } | string>;
    }>;
    count?: number;
    hasMore?: boolean;
    nextCursor?: string | null;
    status?: number;
  }>(API_LIST_BOOKING_BY_AD_AND_BY_USER, { params: { id_user, limit: params?.limit, cursor: params?.cursor || undefined } });
};

export const getBookingsByOwnerAdsApi = (id_owner: string, params?: { limit?: number; cursor?: string | null }) => {
  return axios.get<{
    data: Array<{
      _id?: string;
      user?: any;
      ads?: any;
      bookingDates?: Array<{ date?: string; booked?: boolean | null } | string>;
    }>;
    count?: number;
    hasMore?: boolean;
    nextCursor?: string | null;
    status?: number;
  }>(API_LIST_BOOKING_BY_AD_AND_BY_USER, { params: { id_owner, limit: params?.limit, cursor: params?.cursor || undefined } });
};

export const updateManualBookingClientApi = (
  bookingId: string,
  user: {
    firstName: string;
    lastName: string;
    phone?: string;
    status: string;
    nbr_child: number;
    country?: string;
    indicatif?: string;
    codeCountry?: string;
  },
) => {
  return axios.put<{
    msg?: string;
    message?: string;
    status?: number;
    data?: { bookingId?: string; user?: any };
  }>(`${API_BOOKINGS}/${bookingId}/manual-client`, { user });
};

export const updateBookingReservationColorApi = (
  bookingId: string,
  payload: { groupe?: string; reservationColor: string }
) => {
  return axios.patch<{
    msg?: string;
    status?: number;
    data?: any;
  }>(`${API_BOOKINGS}/${bookingId}/reservation-color`, payload);
};

export const deleteAdCascadeApi = (id: string) => {
  return axios.delete<{ message?: string; status?: number; data?: any }>(`/ads/${id}/cascade`);
};
